import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { ArrowsClockwise, CircleNotch, CreditCard, CheckCircle, Storefront } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function AgricultureSquareSync({ businessId }) {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: txns }] = await Promise.all([
        api.get(`/businesses/${businessId}/square/status`),
        api.get(`/businesses/${businessId}/square/transactions`).catch(() => ({ data: [] })),
      ]);
      setStatus(s);
      setRows(txns || []);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const connect = async () => {
    setWorking(true);
    try {
      await api.post(`/businesses/${businessId}/square/connect`);
      await load();
      toast.success("Square connection structure is ready");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setWorking(false);
    }
  };

  const sync = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/square/sync`);
      await load();
      toast.success(data.rowan_message || "Square sync complete");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setWorking(false);
    }
  };

  const reconcile = async (squareId) => {
    try {
      await api.patch(`/businesses/${businessId}/square/transactions/${squareId}/reconcile`, {});
      await load();
      toast.success("Marked as reconciled");
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="agriculture-square-tab">
      <div className="bk-card p-6" data-testid="agriculture-square-status-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Square Transactions</p>
            <h3 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>Farm stand and market sales sync</h3>
            <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="square-rowan-message">{status?.rowan_message || "Square will keep your market sales tidy once connected."}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={connect} disabled={working} data-testid="square-connect-button" className="bk-btn-secondary px-4 py-2.5 text-sm font-semibold disabled:opacity-60" style={{ color: "var(--bk-text)" }}>
              {working ? <span className="inline-flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Working…</span> : (status?.connected ? "Reconnect Square" : "Connect Square")}
            </button>
            <button onClick={sync} disabled={working} data-testid="square-sync-button" className="bk-btn-primary px-4 py-2.5 text-sm font-semibold disabled:opacity-60">
              {working ? <span className="inline-flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Syncing…</span> : <span className="inline-flex items-center gap-2"><ArrowsClockwise size={16} weight="bold" /> Sync now</span>}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
          <div className="rounded-2xl p-4" style={{ background: "#fff" }} data-testid="square-status-mode">
            <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Mode</p>
            <p className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{status?.mode || "MOCKED"}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: "#fff" }} data-testid="square-status-merchant">
            <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Merchant</p>
            <p className="font-semibold mt-2" style={{ color: "var(--bk-text)" }}>{status?.merchant_name || "Square placeholder"}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ background: "#fff" }} data-testid="square-status-sync">
            <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Last sync</p>
            <p className="font-semibold mt-2" style={{ color: "var(--bk-text)" }}>{status?.last_sync_at || "Not synced yet"}</p>
          </div>
        </div>
        <p className="text-xs mt-4" style={{ color: "var(--bk-text-2)" }} data-testid="square-mocked-note"><strong>MOCKED</strong> until real Square credentials are added. The connect flow, transaction list, categorization, and reconciliation structure are ready.</p>
      </div>

      <div className="bk-card p-6" data-testid="square-transaction-list">
        <div className="flex items-center gap-2 mb-4">
          <Storefront size={18} weight="duotone" color="var(--bk-success)" />
          <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Synced transactions</h4>
        </div>
        {loading ? (
          <div className="py-8 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={18} className="animate-spin" /> Loading Square transactions…</div>
        ) : rows.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Connect and sync to start importing farm stand, market, and CSA sales.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((row, idx) => (
              <div key={row.square_id} className="rounded-2xl border p-4 flex flex-wrap items-center gap-3 justify-between" style={{ borderColor: "var(--bk-border)" }} data-testid={`square-txn-${idx}`}>
                <div>
                  <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{row.merchant_name}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{row.sale_date} · {row.note}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }} data-testid={`square-category-${idx}`}>{row.category}</span>
                  <span className="font-bold" style={{ color: "var(--bk-success)" }}>{money(row.amount)}</span>
                  {row.reconciled ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} data-testid={`square-reconciled-${idx}`}><CheckCircle size={13} weight="fill" /> Matched</span>
                  ) : (
                    <button onClick={() => reconcile(row.square_id)} data-testid={`square-reconcile-${idx}`} className="px-4 py-2 rounded-full border text-xs font-semibold" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                      Mark matched
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}